/**
 * The learner's own budget meter (#443, UX_EXPERIENCE §4.3a).
 *
 * A ring with the percentage of THIS learner's share of the semester's model budget that is used. It is about money,
 * so it is the registered proportion `budget-used`, tagged `data-proportion` (CLAUDE.md, history §12, §19); it is never about what the learner knows,
 * never dollars, and it lives on the landing page only - never on Progress, never beside a status. The no-percentage
 * sweep sets aside only the subtree carrying `data-proportion="budget-used"`, so nothing else on a page may borrow it.
 *
 * Nothing is rationed before empty: no warning at any level. At the lock it says which actions are paused and why.
 * No period, or a request that failed: nothing is rendered (and nothing is locked).
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';

/**
 * The usage sentences, in both languages. The percentage is the one permitted figure (money, not knowledge) and stays
 * LTR inside a `<bdi>`. Other features read `locked`, `modelLockReason` and `rulesOnlyCheck` from here.
 */
export const usageMessages = defineMessages({
  en: {
    ofShare: 'of your share used',
    semester: 'this semester',
    used: (n: number) => `${n}% of your share used`,
    shareUsed: "You have used your share of this semester's model budget.",
    budgetPaused: "This semester's model budget as a whole is used up.",
    locked:
      'Reading homework photos and model-checked answers are paused. Answers checked by exact match or rules stay open.',
    modelLockReason: 'Paused: the model budget is used up. See Home for why.',
    rulesOnlyCheck: 'The model budget is used up, so this check uses rules only.',
  },
  he: {
    ofShare: 'מהחלק שלך בתקציב',
    semester: 'הסמסטר הזה',
    used: (n: number) => `${n}% מהחלק שלך בתקציב`,
    shareUsed: 'ניצלת את החלק שלך בתקציב המודלים של הסמסטר.',
    budgetPaused: 'תקציב המודלים של הסמסטר כולו נוצל.',
    locked:
      'קריאת תמונות של שיעורי בית ובדיקת תשובות באמצעות מודל מושהות. תשובות שנבדקות בהתאמה מדויקת או לפי כללים נשארות פתוחות.',
    modelLockReason: 'מושהה: תקציב המודלים נוצל. הסיבה מופיעה ב"בית".',
    rulesOnlyCheck: 'תקציב המודלים נוצל, ולכן הבדיקה הזו משתמשת בכללים בלבד.',
  },
});

/** English forms, kept for features not yet on the catalogue (#445 pieces 3-4 move them to `usageMessages`). */
export const LOCKED_ACTIONS_SENTENCE = usageMessages.en.locked;

/** What a locked action shows beside itself. */
export const MODEL_LOCK_REASON = usageMessages.en.modelLockReason;

/**
 * What the homework check shows when locked. The check itself stays open: its rule chains are deterministic, and its one
 * model call (the claim proposer, for lines no rule covers) is refused by the ledger and recorded as `budget_refused`.
 */
export const RULES_ONLY_CHECK = usageMessages.en.rulesOnlyCheck;

export function useUsage() {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.usage, queryFn: () => api.getUsage(), retry: false });
}

/** True only when the server said so; a failed or absent answer locks nothing (the server still refuses a call). */
export function useModelLocked(): boolean {
  const usage = useUsage();
  return usage.data?.state === 'metered' && usage.data.locked === true;
}

export function UsageMeter(): React.JSX.Element | null {
  const t = useMessages(usageMessages);
  const usage = useUsage();
  const data = usage.data;
  if (data === undefined || data.state !== 'metered' || data.usedPercent === undefined) return null;
  const used = data.usedPercent;
  return (
    <section className="panel usage-meter" data-proportion="budget-used" aria-labelledby="usage-meter-title">
      <div
        className="usage-meter-ring"
        role="meter"
        aria-labelledby="usage-meter-title"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={used}
        aria-valuetext={t.used(used)}
        style={{ '--used': `${used}%` } as React.CSSProperties}
      />
      <div className="usage-meter-text">
        <h2 id="usage-meter-title">
          <bdi dir="ltr">{used}%</bdi> {t.ofShare}
        </h2>
        <span className="usage-meter-sub">{t.semester}</span>
      </div>
      {data.locked === true ? (
        <div className="notice usage-meter-notice" role="status">
          <p>{data.reason === 'budget_paused' ? t.budgetPaused : t.shareUsed}</p>
          <p>{t.locked}</p>
        </div>
      ) : null}
    </section>
  );
}
